// Every pop-up sheet (`dialog.booking-dialog`: Job done, Book a visit, the payment message, the Diary's windows and the
// rest) closes the way a phone's own sheets do (Liam, 22 Oct 2026, on the payment message: "When i drag down here it
// should close also close should just be a icon on brand but using the word is too much"):
// - its Close is a round X, with "Close" still its name for screen readers and the checks;
// - on a phone, dragging its top bar (the grab bar and heading) down closes it. Let go before it is far enough, or
//   while the sheet is saving (its own Close does nothing then), and it settles back.
// The close itself is always the sheet's own Close button, pressed for you, so each sheet keeps its own rules for
// closing (a save on its way, an Undo offered afterwards). Watched once from start-up (main.ts), for every sheet opened.

const SHEET = 'dialog.booking-dialog'
// Far enough, or fast enough, to mean "close".
const CLOSE_AFTER = 90
const FLICK = 0.6 // px a millisecond

// The sheet's own top bar (a child of the sheet, never one inside its content), and its Close.
function headOf(dialog: HTMLElement): HTMLElement | null {
  return (Array.from(dialog.children).find((c) => c.classList?.contains('booking-head')) as HTMLElement | undefined) ?? null
}
function closeButtonOf(dialog: HTMLElement): HTMLButtonElement | null {
  const head = headOf(dialog)
  if (!head) return null
  const buttons = Array.from(head.children).filter((b): b is HTMLButtonElement => b.tagName === 'BUTTON')
  return buttons.find((b) => /^close\b/i.test(b.getAttribute('aria-label') ?? '') || b.textContent?.trim() === 'Close') ?? null
}

// The word becomes the round X; its name stays (aria-label "Close" when it had none).
export function markSheetClose(dialog: HTMLElement): void {
  const close = closeButtonOf(dialog)
  if (!close || close.classList.contains('sheet-x')) return
  close.classList.add('sheet-x')
  if (!close.getAttribute('aria-label')) close.setAttribute('aria-label', 'Close')
}

const phone = (): boolean => typeof matchMedia === 'function' && matchMedia('(max-width: 760px)').matches
const still = (): boolean => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches

// Drag the top bar down to close (phone only).
function dragToClose(dialog: HTMLElement): void {
  const head = headOf(dialog)
  if (!head || head.dataset.dragClose) return
  head.dataset.dragClose = '1'
  let startY = 0, startAt = 0, dy = 0, dragging = false
  const settle = (): void => {
    dialog.style.transition = still() ? '' : 'transform 200ms cubic-bezier(.2,.8,.2,1)'
    dialog.style.transform = ''
    window.setTimeout(() => { dialog.style.transition = '' }, 220)
  }
  head.addEventListener('touchstart', (e) => {
    if (!phone() || e.touches.length !== 1 || (e.target as Element).closest('button, a, input, select, textarea')) return
    startY = e.touches[0]!.clientY
    startAt = Date.now()
    dy = 0
    dragging = true
    dialog.style.transition = ''
  }, { passive: true })
  head.addEventListener('touchmove', (e) => {
    if (!dragging) return
    dy = Math.max(0, e.touches[0]!.clientY - startY)
    if (dy > 0) e.preventDefault()
    dialog.style.transform = dy ? `translateY(${dy}px)` : ''
  }, { passive: false })
  const end = (): void => {
    if (!dragging) return
    dragging = false
    const fast = dy / Math.max(1, Date.now() - startAt) > FLICK && dy > 24
    if (dy < CLOSE_AFTER && !fast) { settle(); return }
    const close = closeButtonOf(dialog)
    if (!close) { settle(); return }
    close.click()
    // Not closing (a save on its way): back where it was. A sheet that slides away keeps going from where the finger left.
    window.setTimeout(() => {
      if ((dialog as HTMLDialogElement).open && !dialog.classList.contains('sheet-closing')) settle()
    }, 60)
  }
  head.addEventListener('touchend', end)
  head.addEventListener('touchcancel', () => { if (dragging) { dragging = false; settle() } })
}

export function readySheet(dialog: HTMLElement): void {
  if (closeButtonOf(dialog)) { markSheetClose(dialog); dragToClose(dialog); return }
  // Its top bar not drawn yet: watched until it is, then left alone.
  const wait = new MutationObserver(() => {
    if (!closeButtonOf(dialog)) return
    wait.disconnect()
    markSheetClose(dialog)
    dragToClose(dialog)
  })
  wait.observe(dialog, { childList: true, subtree: true })
  dialog.addEventListener('close', () => wait.disconnect(), { once: true })
}

// Every sheet is added straight to the page's body (document.body.append), so only the body's own children are watched.
let watching = false
export function watchSheets(): void {
  if (watching || typeof MutationObserver === 'undefined') return
  watching = true
  for (const d of Array.from(document.querySelectorAll<HTMLElement>(SHEET))) readySheet(d)
  new MutationObserver((records) => {
    for (const r of records) for (const n of Array.from(r.addedNodes)) if (n instanceof HTMLElement && n.matches(SHEET)) readySheet(n)
  }).observe(document.body, { childList: true })
}
